import { SignupFlow } from '../../../../@types/signup.d';
import {
  createResource,
  headRessource,
  readResource,
} from '../../../../actions/api';
import ApiResource from '../../../../api-resources';

export const SIGNUP_WITH_LINKEDIN = '@@signup/SIGNUP_WITH_LINKEDIN';
export const SIGNUP_WITH_VANILLA = '@@signup/SIGNUP_WITH_VANILLA';

export interface SignupWithLinkedinType {
  code: string;
  flow: SignupFlow;
}
export const signupWithLinkedin = (
  {
    code,
    flow,
  }: SignupWithLinkedinType,
  resolve: Function,
  reject: Function,
) => ({
  type: SIGNUP_WITH_LINKEDIN,
  code,
  flow,
  resolve,
  reject,
});

export interface SignupWithVanilla {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  company_name: string;
  flow: SignupFlow;
}
export const signupWithVanilla = (
  {
    firstName,
    lastName,
    email,
    company_name,
    password,
    flow,
  }: SignupWithVanilla,
  resolve: Function,
  reject: Function,
) => ({
  type: SIGNUP_WITH_VANILLA,
  firstName,
  lastName,
  email: email && email.toLowerCase(),
  company_name,
  password,
  flow,
  resolve,
  reject,
});

type RegistrationPathForFlow = {
  [key in keyof typeof SignupFlow]: string;
};
export const registrationPathForFlow: RegistrationPathForFlow = {
  [SignupFlow['school-coach']]: 'school/coaches',
  [SignupFlow.students]: 'students',
  [SignupFlow.mentors]: 'mentors',
  [SignupFlow.ambassador]: 'company/employees',
  [SignupFlow.employee]: 'company/employees',
  [SignupFlow.company_admins]: 'company/admins',
};

export interface CreateUserFromVanilla {
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  company_name: string,
  sign_up_token?: string,
  invitation_token?: string,
  school_token?: string,
  flow: keyof RegistrationPathForFlow,
}
export const createUserFromVanilla = (
  {
    firstName,
    lastName,
    email,
    password,
    company_name,
    sign_up_token,
    invitation_token,
    school_token,
    flow = 'students',
  }: CreateUserFromVanilla,
) => createResource({
  resourceUri: `/registration/${registrationPathForFlow[flow]}`,
  data: {
    type: ApiResource.USER,
    attributes: {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      company_name,
      sign_up_token,
      school_token,
    },
    params: {
      flow,
    },
  },
  meta: {
    invitation_token,
  },
});

export interface CreateUserFromLinkedin {
  identity_id: string,
  sign_up_token?: string,
  invitation_token?: string,
  flow: keyof RegistrationPathForFlow,
}
export const createUserFromLinkedin = (
  {
    identity_id,
    sign_up_token,
    invitation_token,
    flow = 'students',
  }: CreateUserFromLinkedin,
) => createResource({
  resourceUri: `/registration/${registrationPathForFlow[flow]}`,
  data: {
    type: ApiResource.USER,
    attributes: {
      sign_up_token,
      identity_id,
    },
    params: {
      flow,
    },
  },
  meta: {
    invitation_token,
  },
});

export interface GetUserFromLinkedin {
  code: String;
}
export const getUserFromLinkedin = ({ code }: GetUserFromLinkedin) => createResource({
  resourceUri: '/authentications',
  data: {
    type: ApiResource.IDENTITY,
    attributes: {
      authorization_code: code,
      provider: 'linkedin',
      redirect_uri: process.env.REACT_APP_LINKEDIN_CALLBACK_URL,
    },
  },
  meta: {},
});

export interface CheckSignupToken {
  token: string;
}
export const checkSignupToken = ({ token }: CheckSignupToken) => readResource({
  resourceUri: `/mentor_acquisition/sign_up_token/${token}`,
  data: {
    type: ApiResource.MENTOR_ACQUISITION_SIGNUP_TOKEN,
  },
  meta: {},
});

export interface GetUserFromInvitation {
  token: string;
}
export const getEmployeeFromInvitation = ({
  token,
}: GetUserFromInvitation) => readResource({
  resourceUri: '/registration/company/employees/new',
  data: {
    type: ApiResource.PROFILE_EMPLOYEE,
  },
  params: {
    invitation_token: token,
  },
});

export const getCompanyAdminFromInvitation = ({
  token,
}: GetUserFromInvitation) => readResource({
  resourceUri: '/registration/company/admins/new',
  data: {
    type: ApiResource.PROFILE_COMPANY_ADMIN,
  },
  params: {
    invitation_token: token,
  },
});

/**
 * Check a school-coach signup token
 * + retrieve signup data for the school coach
 * if you want to know why this check token endpoint
 * is unlike of the others, ask to Luc
 * @param {Object} param
 * @param {String} params.token
 */
export const checkValidityOfSchoolToken = ({
  token,
}: GetUserFromInvitation) => headRessource({
  resourceUri: `/school_tokens/${token}`,
  data: {
    type: ApiResource.SCHOOL_TOKEN,
  },
});
